import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { useSEO } from "@/lib/useSEO";
import {
  ArrowRight,
  Rocket,
  Globe,
  ShieldCheck,
  Zap,
  TrendingUp,
  Target,
  Users,
  Layers,
  Building2,
  Mail,
  GraduationCap,
} from "lucide-react";

const BigStat = ({ value, label, sub }) => (
  <div className="group bg-[#FAFAF7] border border-[#0B1E3F]/10 p-12 hover:border-[#C9A961] transition-all duration-500 hover:-translate-y-2 hover:shadow-[12px_12px_0_0_#0B1E3F]">
    <div className="font-display font-normal text-5xl md:text-7xl tracking-tight text-[#0B1E3F] group-hover:text-[#C9A961] transition-colors">
      {value}
    </div>
    <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#0B1E3F]/60 mt-6">{label}</div>
    {sub && <div className="text-sm font-light text-[#0B1E3F]/40 mt-3">{sub}</div>}
  </div>
);

const Milestone = ({ phase, items, active }) => (
  <div
    className={`p-10 border transition-all duration-500 ${
      active ? "bg-[#0B1E3F] text-[#FAFAF7] border-[#0B1E3F] shadow-[12px_12px_0_0_#C9A961] -translate-y-2" : "bg-[#FAFAF7] border-[#0B1E3F]/10 hover:border-[#C9A961]"
    }`}
  >
    <div className={`font-sans text-xs uppercase tracking-[0.2em] mb-8 flex items-center gap-3 ${active ? "text-[#C9A961]" : "text-[#0B1E3F]"}`}>
      <span className={`w-6 h-px ${active ? "bg-[#C9A961]" : "bg-[#0B1E3F]/30"}`} /> {phase}
    </div>
    <ul className="space-y-4 text-base font-light">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-4">
          <span className={`w-1.5 h-1.5 mt-2.5 rounded-full flex-shrink-0 ${active ? "bg-[#C9A961]" : "bg-[#0B1E3F]/30"}`} />
          <span className={active ? "text-[#FAFAF7]/80" : "text-[#0B1E3F]/70"}>{i}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Investors() {
  const [traction, setTraction] = useState(null);
  useSEO({
    title: "For Investors — Maqam",
    description: "Maqam is Saudi Arabia's AI career infrastructure — traction, market, unit economics, and the Vision 2030 opportunity.",
    path: "/for-investors",
  });

  useEffect(() => {
    api.get("/public/traction").then((r) => setTraction(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0B1E3F]" data-testid="investors-page">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-[#0B1E3F]/10 relative overflow-hidden bg-[#FAFAF7]">
        <div className="grain-overlay" />
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201, 169, 97, 0.1) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(11, 30, 63, 0.05) 0, transparent 40%)",
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-48">
          <div className="grid md:grid-cols-12 gap-16 items-center">
            <div className="md:col-span-8 fade-up">
              <div className="inline-flex items-center gap-3 border border-[#C9A961]/50 bg-[#FAFAF7] px-5 py-2 mb-10 font-sans text-xs uppercase tracking-[0.2em] text-[#0B1E3F] shadow-[4px_4px_0_0_#C9A961]">
                <span className="w-2 h-2 rounded-full bg-[#C9A961]" /> For investors &amp; strategic partners
              </div>
              <h1 className="font-display font-normal text-6xl md:text-8xl lg:text-[7rem] tracking-tight leading-[0.9] mb-10">
                We're building the
                <br />
                <span className="italic text-[#C9A961]">AI career OS</span>
                <br />
                of Saudi Arabia.
              </h1>
              <p className="text-[#0B1E3F]/70 text-xl md:text-2xl max-w-3xl font-light leading-relaxed mb-12">
                Vision 2030 demands 1M+ Saudi nationals into private-sector jobs. Maqam is the bilingual,
                AI-first platform that closes the gap — bulk-applies to 100 jobs in one click, ranks fit
                with Claude Sonnet 4.5, and unlocks Nitaqat-optimized career paths.
              </p>
              <div className="flex flex-wrap gap-6">
                <a
                  href="mailto:founder@maqam-ksa.com?subject=Maqam%20—%20Investment%20conversation"
                  className="bg-[#0B1E3F] text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0B1E3F] font-sans font-medium text-lg px-10 py-6 flex items-center justify-center gap-3 transition-all duration-300 border border-[#0B1E3F] shadow-[8px_8px_0_0_#C9A961] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0_0_#0B1E3F]"
                  data-testid="investors-contact"
                >
                  Talk to the founder <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/downloads/maqam-pilot-kit.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border border-[#0B1E3F]/20 text-[#0B1E3F] hover:border-[#0B1E3F] font-sans font-medium text-lg px-10 py-6 transition-all duration-300 flex items-center justify-center"
                  data-testid="investors-pilot-kit"
                >
                  Pilot outreach kit
                </a>
              </div>
            </div>
            <div className="md:col-span-4 fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="border border-[#0B1E3F] bg-[#FAFAF7] p-10 shadow-[12px_12px_0_0_#C9A961] space-y-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#0B1E3F]/5 rounded-bl-full pointer-events-none" />
                
                <div>
                  <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#C9A961] mb-2">Stage</div>
                  <div className="font-display font-normal text-3xl text-[#0B1E3F]">Pre-seed</div>
                </div>
                <div className="h-px bg-[#0B1E3F]/10 w-full" />
                
                <div>
                  <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#C9A961] mb-2">Raising</div>
                  <div className="font-display font-normal text-3xl text-[#0B1E3F]">TBD <span className="text-[#0B1E3F]/40 italic text-xl">via email</span></div>
                </div>
                <div className="h-px bg-[#0B1E3F]/10 w-full" />
                
                <div>
                  <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#C9A961] mb-2">HQ</div>
                  <div className="font-display font-normal text-3xl text-[#0B1E3F]">KSA <span className="text-[#0B1E3F]/40 italic text-xl">founding</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Traction */}
      <section className="border-b border-[#0B1E3F]/10 bg-[#FAFAF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-40">
          <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C9A961]" /> Traction
          </div>
          <h2 className="font-display font-normal text-5xl md:text-7xl tracking-tight mb-20 text-[#0B1E3F]">
            Early signal. Real users. Real jobs.
          </h2>
          {traction && (
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-0" data-testid="investors-traction">
              <BigStat value={`${traction.users}+`} label="Users on the platform" />
              <BigStat value={`${traction.applications_sent}+`} label="Applications sent" />
              <BigStat value={`${traction.live_jobs}+`} label="Live KSA jobs indexed" sub="Jooble API · 6h refresh" />
              <BigStat value={`${traction.companies}+`} label="KSA companies" />
              <BigStat value={`${traction.cities}`} label="Cities covered" />
            </div>
          )}
        </div>
      </section>

      {/* Why now */}
      <section className="border-b border-[#0B1E3F]/10 bg-[#0B1E3F] text-[#FAFAF7] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9IiNDOUE5NjEiIGZpbGwtb3BhY2l0eT0iMC4xNSIvPjwvc3ZnPg==')] opacity-30" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-40">
          <div className="grid md:grid-cols-12 gap-16">
            <div className="md:col-span-5">
              <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-6 flex items-center gap-3">
                <span className="w-8 h-px bg-[#C9A961]" /> Why now
              </div>
              <h2 className="font-display font-normal text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1]">
                Three tailwinds colliding at once.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-16">
              {[
                {
                  icon: Rocket,
                  title: "Vision 2030 · Saudization mandate",
                  body: "Saudi govt targeting 1M+ private-sector Saudi hires. Nitaqat compliance is now enforced via MOL audits.",
                },
                {
                  icon: Zap,
                  title: "AI-first job matching",
                  body: "LLMs can finally read unstructured CVs + JDs at scale. Legacy boards (Bayt, Indeed, LinkedIn) are keyword-only — Maqam is the first KSA-native AI layer.",
                },
                {
                  icon: Globe,
                  title: "Bilingual by default",
                  body: "EN + AR with RTL from day one. KSA candidates apply in Arabic, expats apply in English — same platform, same pipeline.",
                },
              ].map((i) => (
                <div key={i.title} className="flex flex-col sm:flex-row gap-8 group">
                  <div className="w-16 h-16 rounded-full border border-[#C9A961]/30 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A961] transition-colors duration-500">
                    <i.icon className="w-7 h-7 text-[#C9A961] group-hover:text-[#0B1E3F] transition-colors stroke-[1.5]" />
                  </div>
                  <div>
                    <div className="font-display font-normal text-3xl mb-4">{i.title}</div>
                    <p className="text-[#FAFAF7]/60 text-lg font-light leading-relaxed">{i.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="border-b border-[#0B1E3F]/10 bg-[#FAFAF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-40">
          <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C9A961]" /> Roadmap
          </div>
          <h2 className="font-display font-normal text-5xl md:text-7xl tracking-tight mb-20 text-[#0B1E3F]">
            Shipped. Shipping. Next.
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            <Milestone
              phase="Shipped · 2026 Q1"
              items={[
                "AI job search + ranking (Claude Sonnet 4.5)",
                "Bulk apply — 100 jobs / click",
                "Bilingual EN + AR with RTL",
                "Jooble live KSA job feed (6h refresh)",
                "Resend outbound email",
                "Stripe + Moyasar dual checkout",
                "CV Builder — 10 templates, custom colors, auto-PDF",
                "Certificates & Courses + AI Cert→Job match",
              ]}
            />
            <Milestone
              phase="Shipping now"
              active
              items={[
                "Admin + analytics dashboard",
                "PDPL-aligned data export / delete",
                "Skill Gap Alerts — weekly retention loop",
                "Investor-ready landing (this page)",
              ]}
            />
            <Milestone
              phase="Next · 2026 Q2-Q3"
              items={[
                "Two-sided Employer Marketplace (paid)",
                "Company Intelligence data moat",
                "Public API + Zapier",
                "AI Voice Mock Interview",
                "University / training center white-label",
                "Mobile app (React Native)",
              ]}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#C9A961] text-[#0B1E3F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 md:py-40 grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-7">
            <Target className="w-12 h-12 mb-8 text-[#0B1E3F] stroke-[1.5]" />
            <h2 className="font-display font-normal text-6xl md:text-7xl tracking-tight leading-[0.95] text-[#0B1E3F]">
              Let's compare <span className="italic">diaries.</span>
            </h2>
            <p className="text-[#0B1E3F]/80 mt-8 text-2xl font-light max-w-xl leading-relaxed">
              If you back AI-first career-tech and you're looking at MENA, we should talk.
            </p>
          </div>
          <div className="md:col-span-5 space-y-6">
            <a
              href="mailto:founder@maqam-ksa.com?subject=Maqam%20—%20Investment"
              className="flex items-center justify-between border border-[#0B1E3F]/20 hover:border-[#0B1E3F] bg-[#FAFAF7]/5 hover:bg-[#FAFAF7] p-8 transition-all duration-500 group"
              data-testid="investors-email"
            >
              <div>
                <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#0B1E3F]/50 mb-3 group-hover:text-[#C9A961] transition-colors">Email</div>
                <div className="font-display font-normal text-2xl">founder@maqam-ksa.com</div>
              </div>
              <Mail className="w-6 h-6 text-[#0B1E3F] group-hover:text-[#C9A961] transition-colors stroke-[1.5] group-hover:scale-110" />
            </a>
            <Link
              to="/subscriptions"
              className="flex items-center justify-between border border-[#0B1E3F]/20 hover:border-[#0B1E3F] bg-[#FAFAF7]/5 hover:bg-[#FAFAF7] p-8 transition-all duration-500 group"
            >
              <div>
                <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#0B1E3F]/50 mb-3 group-hover:text-[#C9A961] transition-colors">Product</div>
                <div className="font-display font-normal text-2xl">Explore subscription tiers</div>
              </div>
              <Layers className="w-6 h-6 text-[#0B1E3F] group-hover:text-[#C9A961] transition-colors stroke-[1.5] group-hover:scale-110" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#0B1E3F]/10 bg-[#FAFAF7] text-[#0B1E3F]/60 text-sm font-sans">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row gap-8 justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-[#0B1E3F] text-[#C9A961] flex items-center justify-center font-display font-black text-xs">M</div>
            <span>© 2026 Maqam. Built for the Kingdom.</span>
          </div>
          <div className="flex gap-8 uppercase tracking-widest text-xs font-medium">
            <Link to="/privacy" className="hover:text-[#0B1E3F] transition-colors">Privacy (PDPL)</Link>
            <span className="hover:text-[#0B1E3F] cursor-pointer transition-colors">Terms</span>
            <a href="mailto:founder@maqam-ksa.com" className="hover:text-[#0B1E3F] transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
